import { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next'; // 1. Import hook

const CustomCalendar = ({ datevalue, onDateValueChange }) => {
  const { t, i18n } = useTranslation(); // 2. Initialize hook

  // Parse date range from props
  const parseDateRange = (ranges) => {
    if (!ranges || !Array.isArray(ranges) || ranges.length === 0) return null;
    const range = ranges[0];
    return {
      start: new Date(range.startDate || new Date()),
      end: new Date(range.endDate || new Date())
    };
  };

  const today = new Date();
  const parsed = parseDateRange(datevalue);
  
  const [currentMonth, setCurrentMonth] = useState(parsed?.start.getMonth() || today.getMonth());
  const [currentYear, setCurrentYear] = useState(parsed?.start.getFullYear() || today.getFullYear());
  const [selectedRange, setSelectedRange] = useState({
    start: parsed?.start || null,
    end: parsed?.end || null
  });
  const [hoverDate, setHoverDate] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // 3. REMOVED hardcoded 'months' and 'daysOfWeek' arrays. 
  // We will generate them dynamically based on locale below.

  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;
  const showTwoCalendars = windowWidth >= 900;

  useEffect(() => {
    const parsed = parseDateRange(datevalue);
    if (parsed) {
      setSelectedRange(parsed);
      setCurrentMonth(parsed.start.getMonth());
      setCurrentYear(parsed.start.getFullYear());
    }
  }, [datevalue]);

  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, 150);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  // 4. Wrapped preset labels in t()
  const presets = [
    { 
      label: t('calendar.presets.today', 'Today'), 
      getValue: () => ({ start: today, end: today }) 
    },
    { 
      label: t('calendar.presets.yesterday', 'Yesterday'), 
      getValue: () => {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { start: yesterday, end: yesterday };
      }
    },
    { 
      label: t('calendar.presets.last7Days', 'Last 7 Days'), 
      getValue: () => {
        const start = new Date(today);
        start.setDate(start.getDate() - 6);
        return { start, end: today };
      }
    },
    { 
      label: t('calendar.presets.last30Days', 'Last 30 Days'), 
      getValue: () => {
        const start = new Date(today);
        start.setDate(start.getDate() - 29);
        return { start, end: today };
      }
    },
    { 
      label: t('calendar.presets.thisMonth', 'This Month'), 
      getValue: () => {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start, end: today };
      }
    },
    { 
      label: t('calendar.presets.lastMonth', 'Last Month'), 
      getValue: () => {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 0);
        return { start, end };
      }
    }
  ];

  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.toDateString() === date2.toDateString();
  };

  const isInRange = (date) => {
    if (!selectedRange.start || !selectedRange.end) return false;
    return date >= selectedRange.start && date <= selectedRange.end;
  };

  const isInHoverRange = (date) => {
    if (!selectedRange.start || !hoverDate || selectedRange.end) return false;
    const start = selectedRange.start < hoverDate ? selectedRange.start : hoverDate;
    const end = selectedRange.start < hoverDate ? hoverDate : selectedRange.start;
    return date >= start && date <= end;
  };

  const handleDateClick = (date) => {
    if (!isSelecting) {
      setSelectedRange({ start: date, end: null });
      setIsSelecting(true);
    } else {
      if (date < selectedRange.start) {
        setSelectedRange({ start: date, end: selectedRange.start });
      } else {
        setSelectedRange({ start: selectedRange.start, end: date });
      }
      setIsSelecting(false);
    }
  };

  const handlePresetClick = (preset) => {
    const range = preset.getValue();
    setSelectedRange(range);
    setIsSelecting(false);
    setCurrentMonth(range.start.getMonth());
    setCurrentYear(range.start.getFullYear());
  };

  const handleApply = () => {
    if (selectedRange.start && selectedRange.end) {
      onDateValueChange([{
        startDate: selectedRange.start,
        endDate: selectedRange.end,
        key: 'selection'
      }]);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    }
  };

  const handleClear = () => {
    setSelectedRange({ start: null, end: null });
    setIsSelecting(false);
  };

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  // Helper to generate weekdays dynamically based on locale
  const getWeekdays = () => {
    const days = [];
    const curr = new Date();
    // Start from Sunday (or Monday depending on preference, logic below assumes Sunday start)
    const first = curr.getDate() - curr.getDay(); 
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(curr.setDate(first + i));
      days.push(day.toLocaleString(i18n.language, { weekday: 'short' }));
    }
    return days;
  };
  
  const weekDays = getWeekdays();

  const renderCalendar = (monthOffset = 0) => {
    const month = (currentMonth + monthOffset + 12) % 12;
    const year = currentYear + Math.floor((currentMonth + monthOffset) / 12);
    const daysInMonth = getDaysInMonth(month, year);
    const firstDay = getFirstDayOfMonth(month, year);
    const days = [];

    // 5. Dynamic Month Name translation
    const monthName = new Date(year, month).toLocaleString(i18n.language, { month: 'long' });

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day-cell" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isToday = isSameDay(date, today);
      const isStart = isSameDay(date, selectedRange.start);
      const isEnd = isSameDay(date, selectedRange.end);
      const inRange = isInRange(date);
      const inHover = isInHoverRange(date);
      const isFuture = date > today;
      const isPast = date < new Date(2020, 0, 1);

      days.push(
        <button
          key={day}
          onClick={() => !isFuture && !isPast && handleDateClick(date)}
          onMouseEnter={() => !isFuture && !isPast && setHoverDate(date)}
          disabled={isFuture || isPast}
          className={`
            calendar-day-cell calendar-day-button
            ${isFuture || isPast ? 'day-disabled' : 'day-enabled'}
            ${isToday && !isStart && !isEnd ? 'day-today' : ''}
            ${inRange || inHover ? 'day-in-range' : ''}
            ${isStart || isEnd ? 'day-selected' : ''}
            ${isStart ? 'day-start' : ''}
            ${isEnd ? 'day-end' : ''}
          `}
        >
          <span className="relative z-10">{day}</span>
          {isToday && !isStart && !isEnd && (
            <div className="day-today-indicator" />
          )}
        </button>
      );
    }

    return (
      <div className="calendar-month-container">
        <div className="calendar-month-header">
          {monthName} {year}
        </div>
        <div className="calendar-weekdays">
          {weekDays.map((day, index) => (
            <div key={index} className="calendar-weekday">
              {isMobile ? day.charAt(0) : day}
            </div>
          ))}
        </div>
        <div className="calendar-days-grid">
          {days}
        </div>
      </div>
    );
  };

  const formatDate = (date) => {
    if (!date) return t('calendar.selectDate', 'Select date');
    const options = isMobile 
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' };
      
    // 6. Use i18n.language instead of 'en-US'
    return date.toLocaleDateString(i18n.language, options);
  };

  return (
    <div className="calendar-wrapper">
      {showSuccess && (
        <div className="success-toast">
          <Check size={isMobile ? 16 : 20} />
          <span>{t('calendar.success', 'Date range applied!')}</span>
        </div>
      )}

      <div className={`calendar-container ${isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'}`}>
        <div className="presets-sidebar">
          <h3 className="presets-title">{t('calendar.quickSelect', 'Quick Select')}</h3>
          <div className="presets-grid">
            {presets.map((preset, idx) => {
              const range = preset.getValue();
              const isSelected = isSameDay(selectedRange.start, range.start) && 
                               isSameDay(selectedRange.end, range.end);
              return (
                <button
                  key={idx}
                  onClick={() => handlePresetClick(preset)}
                  className={`preset-button ${isSelected ? 'selected' : ''}`}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="calendar-main">
          <div className="date-display-container">
            <div className="date-display-box">
              <div className="date-display-label">{t('calendar.startDate', 'Start Date')}</div>
              <div className="date-display-value">{formatDate(selectedRange.start)}</div>
            </div>
            {!isMobile && <div className="date-separator">→</div>}
            <div className="date-display-box">
              <div className="date-display-label">{t('calendar.endDate', 'End Date')}</div>
              <div className="date-display-value">{formatDate(selectedRange.end)}</div>
            </div>
          </div>

          <div className="month-navigation">
            <button
              onClick={() => navigateMonth('prev')}
              className="nav-button"
              aria-label={t('calendar.prevMonth', 'Previous month')}
            >
              <ChevronLeft size={isMobile ? 18 : 20} />
            </button>
            
            <div className="nav-title">
              <CalendarIcon size={isMobile ? 16 : 18} className="text-green-600" />
              <span>{t('calendar.selectRange', 'Select Date Range')}</span>
            </div>

            <button
              onClick={() => navigateMonth('next')}
              disabled={currentMonth === today.getMonth() && currentYear === today.getFullYear()}
              className="nav-button"
              aria-label={t('calendar.nextMonth', 'Next month')}
            >
              <ChevronRight size={isMobile ? 18 : 20} />
            </button>
          </div>

          <div className="calendars-container">
            {renderCalendar(0)}
            {showTwoCalendars && renderCalendar(1)}
          </div>

          <div className="action-buttons">
            <button
              onClick={handleClear}
              disabled={!selectedRange.start && !selectedRange.end}
              className="clear-button"
            >
              <X size={16} />
              <span>{t('common.clear', 'Clear')}</span>
            </button>
            <button
              onClick={handleApply}
              disabled={!selectedRange.start || !selectedRange.end}
              className="apply-button"
            >
              {t('calendar.apply', 'Apply')}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .calendar-wrapper {
          width: 100%;
          height: 100%;
          max-width: 100%;
          padding: clamp(12px, 3vw, 32px);
          background: white;
          border-radius: clamp(8px, 2vw, 16px);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          box-sizing: border-box;
          overflow: auto;
        }

        .success-toast {
          position: fixed;
          top: 16px;
          left: 50%;
          transform: translateX(-50%);
          background: #16a34a;
          color: white;
          padding: 12px 20px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          display: flex;
          align-items: center;
          gap: 8px;
          z-index: 1000;
          animation: fade-in 0.3s ease-out;
          font-size: clamp(13px, 2vw, 15px);
        }

        .calendar-container {
          display: flex;
          gap: clamp(16px, 3vw, 24px);
          width: 100%;
          max-width: 100%;
        }

        .calendar-container.mobile {
          flex-direction: column;
        }

        .calendar-container.tablet,
        .calendar-container.desktop {
          flex-direction: row;
        }

        .presets-sidebar {
          flex-shrink: 0;
        }

        .calendar-container.mobile .presets-sidebar {
          width: 100%;
        }

        .calendar-container.tablet .presets-sidebar,
        .calendar-container.desktop .presets-sidebar {
          width: clamp(140px, 15vw, 200px);
        }

        .presets-title {
          font-size: clamp(12px, 2vw, 14px);
          font-weight: 600;
          color: #374151;
          margin-bottom: 12px;
        }

        .presets-grid {
          display: grid;
          gap: 4px;
        }

        .calendar-container.mobile .presets-grid {
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
        }

        .calendar-container.tablet .presets-grid,
        .calendar-container.desktop .presets-grid {
          grid-template-columns: 1fr;
        }

        .preset-button {
          width: 100%;
          text-align: left;
          padding: clamp(8px, 2vw, 10px) clamp(10px, 2vw, 12px);
          border-radius: 6px;
          font-size: clamp(11px, 1.8vw, 13px);
          transition: all 0.2s;
          border: none;
          background: transparent;
          color: #374151;
          cursor: pointer;
        }

        .preset-button:hover {
          background: #f3f4f6;
        }

        .preset-button.selected {
          background: #dcfce7;
          color: #15803d;
          font-weight: 500;
        }

        .calendar-main {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: clamp(12px, 2vw, 20px);
        }

        .date-display-container {
          display: flex;
          align-items: center;
          gap: clamp(8px, 2vw, 12px);
        }

        .calendar-container.mobile .date-display-container {
          flex-direction: column;
          align-items: stretch;
        }

        .date-display-box {
          flex: 1;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: clamp(10px, 2vw, 12px);
        }

        .date-display-label {
          font-size: clamp(10px, 1.5vw, 11px);
          color: #6b7280;
          margin-bottom: 4px;
        }

        .date-display-value {
          font-weight: 500;
          color: #1f2937;
          font-size: clamp(12px, 2vw, 14px);
        }

        .date-separator {
          color: #9ca3af;
          font-size: clamp(16px, 2vw, 20px);
        }

        .month-navigation {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: clamp(8px, 1vw, 12px) 0;
        }

        .nav-button {
          padding: clamp(6px, 1vw, 8px);
          border: none;
          background: transparent;
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .nav-button:hover:not(:disabled) {
          background: #f3f4f6;
        }

        .nav-button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .nav-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: clamp(11px, 2vw, 13px);
          font-weight: 500;
          color: #6b7280;
        }

        .calendars-container {
          display: flex;
          gap: clamp(16px, 3vw, 32px);
          justify-content: center;
          flex-wrap: wrap;
        }

        .calendar-month-container {
          flex: 1;
          min-width: min(100%, 240px);
          max-width: 400px;
        }

        .calendar-month-header {
          text-align: center;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: clamp(12px, 2vw, 16px);
          font-size: clamp(14px, 2.5vw, 17px);
        }

        .calendar-weekdays {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: clamp(2px, 0.5vw, 4px);
          margin-bottom: clamp(6px, 1vw, 8px);
        }

        .calendar-weekday {
          text-align: center;
          font-size: clamp(10px, 1.5vw, 12px);
          font-weight: 600;
          color: #6b7280;
          padding: clamp(4px, 1vw, 6px) 0;
        }

        .calendar-days-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: clamp(2px, 0.5vw, 4px);
        }

        .calendar-day-cell {
          aspect-ratio: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          font-size: clamp(11px, 2vw, 14px);
        }

        .calendar-day-button {
          border: none;
          background: transparent;
          transition: all 0.15s;
          border-radius: 4px;
          width: 100%;
          cursor: pointer;
        }

        .day-enabled:hover {
          background: #dcfce7;
        }

        .day-disabled {
          color: #d1d5db;
          cursor: not-allowed;
        }

        .day-today {
          font-weight: 700;
          color: #16a34a;
        }

        .day-in-range {
          background: #dcfce7;
        }

        .day-selected {
          background: #16a34a !important;
          color: white !important;
          font-weight: 600;
        }

        .day-start {
          border-radius: 50% 4px 4px 50%;
        }

        .day-end {
          border-radius: 4px 50% 50% 4px;
        }

        .day-today-indicator {
          position: absolute;
          bottom: clamp(2px, 0.5vw, 3px);
          left: 50%;
          transform: translateX(-50%);
          width: clamp(3px, 0.8vw, 4px);
          height: clamp(3px, 0.8vw, 4px);
          background: #16a34a;
          border-radius: 50%;
        }

        .action-buttons {
          display: flex;
          justify-content: flex-end;
          gap: clamp(8px, 2vw, 12px);
          padding-top: clamp(8px, 1vw, 12px);
        }

        .calendar-container.mobile .action-buttons {
          flex-direction: column;
        }

        .clear-button,
        .apply-button {
          padding: clamp(8px, 2vw, 10px) clamp(16px, 3vw, 24px);
          border-radius: 8px;
          font-weight: 500;
          font-size: clamp(12px, 2vw, 14px);
          transition: all 0.2s;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .clear-button {
          border: 1px solid #d1d5db;
          background: transparent;
          color: #374151;
        }

        .clear-button:hover:not(:disabled) {
          background: #f9fafb;
        }

        .clear-button:disabled {
          border-color: #e5e7eb;
          color: #9ca3af;
          cursor: not-allowed;
        }

        .apply-button {
          background: #16a34a;
          color: white;
          border: none;
        }

        .apply-button:hover:not(:disabled) {
          background: #15803d;
          box-shadow: 0 4px 12px rgba(22, 163, 74, 0.3);
        }

        .apply-button:disabled {
          background: #e5e7eb;
          color: #9ca3af;
          cursor: not-allowed;
        }

        @keyframes fade-in {
          from { 
            opacity: 0; 
            transform: translate(-50%, -10px); 
          }
          to { 
            opacity: 1; 
            transform: translate(-50%, 0); 
          }
        }

        @media (max-width: 640px) {
          .calendar-wrapper {
            padding: 16px;
            border-radius: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default CustomCalendar;